import React, { useEffect, useState } from "react";
import {
  FiCalendar,
  FiMapPin,
  FiPlusSquare,
  FiTarget,
  FiCamera,
  FiImage,
  FiNavigation,
  FiClipboard,
  FiDownload,
} from "react-icons/fi";
import "./sfaKeyFeatures.css";
import SfaBookingModal from "./sfaBookingModal";

const features = [
  {
    id: "01",
    title: "Scheduled Visits via Beats",
    shortTitle: "Beat-Based Visits",
    description:
      "Plan and organize daily outlet coverage through predefined beats. Sales representatives can see the outlets assigned to their beat and work through the planned visits in a structured sequence, giving supervisors a clearer view of planned versus executed field activity.",
    icon: <FiCalendar />,
    accent: "orange",
    metric: "PLANNED FIELD COVERAGE",
    visualTitle: "Today's Beat",
    visualValue: "12 Outlets",
    steps: [
      "Sales representative opens the assigned beat.",
      "Planned outlets are available for the day's visit activity.",
      "Representative executes the scheduled outlet visits.",
    ],
  },
  {
    id: "02",
    title: "Unscheduled Visits",
    shortTitle: "Unscheduled Visits",
    description:
      "Field sales does not always happen according to plan. Representatives can handle additional customer opportunities by creating and executing unscheduled visits directly from the field, keeping unexpected market activity part of the same sales workflow.",
    icon: <FiMapPin />,
    accent: "cyan",
    metric: "FLEXIBLE FIELD EXECUTION",
    visualTitle: "Additional Visit",
    visualValue: "On Demand",
    steps: [
      "Representative identifies an additional outlet opportunity.",
      "An unscheduled visit is initiated from the field.",
      "The visit becomes part of the day's recorded activity.",
    ],
  },
  {
    id: "03",
    title: "Create Outlets from the Field",
    shortTitle: "Create Outlets",
    description:
      "Sales representatives can create new outlet records without waiting to return to the office. Outlet information can be captured directly during market coverage, helping the business expand its outlet universe while keeping new customer information connected to field execution.",
    icon: <FiPlusSquare />,
    accent: "teal",
    metric: "OUTLET EXPANSION",
    visualTitle: "New Outlet",
    visualValue: "Captured",
    steps: [
      "Representative identifies a new potential outlet.",
      "Outlet information and address details are entered.",
      "The new outlet becomes part of the business's outlet records.",
    ],
  },
  {
    id: "04",
    title: "100-Meter Radius Validation",
    shortTitle: "100m Check-In",
    description:
      "Outlet check-in can be validated against a 100-meter radius around the outlet location. This adds location-based verification to the visit workflow and helps ensure that a representative is physically near the relevant outlet while performing the check-in.",
    icon: <FiTarget />,
    accent: "orange",
    metric: "LOCATION VALIDATION",
    visualTitle: "Outlet Check-In",
    visualValue: "100m Radius",
    steps: [
      "The application captures the representative's location.",
      "The location is checked against the outlet location.",
      "The visit can proceed when the location falls within the configured radius.",
    ],
  },
  {
    id: "05",
    title: "Mandatory Selfie During Attendance",
    shortTitle: "Selfie Attendance",
    description:
      "Attendance can require a selfie as part of the attendance workflow. This connects the attendance record with a visual confirmation captured through the mobile application when the representative starts the field working day.",
    icon: <FiCamera />,
    accent: "coral",
    metric: "ATTENDANCE VERIFICATION",
    visualTitle: "Attendance",
    visualValue: "Selfie Required",
    steps: [
      "Representative starts the attendance process.",
      "A selfie is captured through the mobile application.",
      "Attendance is recorded as the starting point for the field day.",
    ],
  },
  {
    id: "06",
    title: "Image Uploads While Creating Outlets",
    shortTitle: "Outlet Images",
    description:
      "Outlet creation can include image capture and uploads directly from the field. Representatives can add outlet photographs along with the outlet information, creating a richer customer record that can support identification and field-level visibility.",
    icon: <FiImage />,
    accent: "purple",
    metric: "RICHER OUTLET INFORMATION",
    visualTitle: "Outlet Photos",
    visualValue: "Images Added",
    steps: [
      "Representative captures outlet photographs.",
      "Images are associated with the outlet creation workflow.",
      "The outlet record retains richer visual information.",
    ],
  },
  {
    id: "07",
    title: "Real-Time Live Tracking of Salesperson",
    shortTitle: "Live Tracking",
    description:
      "Salesperson location can be tracked during field operations, giving management greater visibility into where representatives are operating during the working day. This creates a live operational view of field movement instead of relying only on end-of-day reporting.",
    icon: <FiNavigation />,
    accent: "cyan",
    metric: "REAL-TIME FIELD VISIBILITY",
    visualTitle: "Salesperson",
    visualValue: "Live Tracking",
    steps: [
      "The salesperson's mobile device captures location information.",
      "Field movement contributes to the live tracking view.",
      "Management can use the available tracking information to understand field activity.",
    ],
  },
  {
    id: "08",
    title: "Visit Details During Outlet Checkout",
    shortTitle: "Visit Checkout",
    description:
      "Completing an outlet visit is more than simply marking it as finished. During checkout, the field workflow can capture the relevant visit details, including whether an order was generated and the outcome or information recorded for that customer interaction.",
    icon: <FiClipboard />,
    accent: "teal",
    metric: "COMPLETE VISIT RECORD",
    visualTitle: "Visit Checkout",
    visualValue: "Completed",
    steps: [
      "Representative completes the customer interaction.",
      "Visit details and outcome are recorded during checkout.",
      "The completed visit becomes part of the day's activity history.",
    ],
  },
  {
    id: "09",
    title: "Daily Travel History Download in Excel",
    shortTitle: "Travel History",
    description:
      "Daily travel history can be downloaded in Excel format for operational review and reporting. This gives teams a practical way to examine salesperson movement and travel activity beyond the live tracking screen.",
    icon: <FiDownload />,
    accent: "orange",
    metric: "TRAVEL REPORTING",
    visualTitle: "Daily Travel History",
    visualValue: "Excel Export",
    steps: [
      "The day's travel and movement information is consolidated.",
      "The travel history is prepared for reporting.",
      "The daily travel information can be downloaded in Excel format.",
    ],
  },
];

const SfaKeyFeatures = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [showSfaDemo, setShowSfaDemo] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((previous) =>
        previous === features.length - 1 ? 0 : previous + 1,
      );
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  const active = features[activeFeature];

  return (
    <>
      <section className="sfa-key-features-section">
        <div className="sfa-key-features-orb sfa-key-features-orb-left"></div>
        <div className="sfa-key-features-orb sfa-key-features-orb-right"></div>

        <div className="container">
          <div className="sfa-key-features-heading" data-aos="fade-up">
            <span className="sfa-key-features-kicker">
              <span className="sfa-key-features-kicker-dot"></span>
              KEY SFA FEATURES
            </span>

            <h2>
              Built Around Your
              <span> Real Field-Sales Workflow</span>
            </h2>

            <p>
              From planning the first outlet visit to tracking field movement
              and reviewing daily travel history, TechCognics SFA brings the
              detailed activities of your sales team into one connected mobile
              and management workflow.
            </p>
          </div>

          <div className="sfa-key-features-layout">
            <div className="sfa-key-features-list" data-aos="fade-right">
              {features.map((feature, index) => (
                <button
                  type="button"
                  key={feature.id}
                  className={`sfa-feature-list-item ${
                    activeFeature === index ? "active" : ""
                  }`}
                  onClick={() => setActiveFeature(index)}
                >
                  <span className="sfa-feature-list-number">{feature.id}</span>

                  <span
                    className={`sfa-feature-list-icon sfa-feature-${feature.accent}`}
                  >
                    {feature.icon}
                  </span>

                  <span className="sfa-feature-list-content">
                    <span className="sfa-feature-list-title">
                      {feature.title}
                    </span>
                  </span>

                  <span className="sfa-feature-list-arrow">→</span>
                </button>
              ))}
            </div>

            <div
              className="sfa-feature-showcase"
              data-aos="fade-left"
              data-aos-delay="120"
            >
              <div className="sfa-feature-showcase-top">
                <div>
                  <span>{active.metric}</span>
                  <small>TECHCOGNICS SFA</small>
                </div>

                <span className="sfa-feature-counter">
                  {active.id} / {String(features.length).padStart(2, "0")}
                </span>
              </div>

              <div className="sfa-feature-showcase-main">
                <div
                  className={`sfa-feature-icon-large sfa-feature-large-${active.accent}`}
                >
                  {active.icon}
                </div>

                <h3>{active.title}</h3>

                <p className="sfa-feature-description">{active.description}</p>

                <div className="sfa-feature-how">
                  <div className="sfa-feature-how-header">
                    <span>HOW IT WORKS</span>

                    <div className="sfa-feature-live">
                      <span></span>
                      FIELD WORKFLOW
                    </div>
                  </div>

                  <div className="sfa-feature-steps">
                    {active.steps.map((step, index) => (
                      <div
                        className="sfa-feature-step"
                        key={step}
                        style={{
                          animationDelay: `${index * 140}ms`,
                        }}
                      >
                        <div className="sfa-feature-step-number">
                          {String(index + 1).padStart(2, "0")}
                        </div>

                        <div className="sfa-feature-step-line"></div>

                        <div className="sfa-feature-step-text">{step}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sfa-feature-product-preview">
                  <div className="sfa-preview-grid"></div>

                  <div className="sfa-preview-content">
                    <div className="sfa-preview-header">
                      <span>{active.visualTitle}</span>

                      <span className="sfa-preview-status">
                        <i></i>
                        ACTIVE
                      </span>
                    </div>

                    <strong>{active.visualValue}</strong>

                    <div className="sfa-preview-bar">
                      <span></span>
                    </div>

                    <div className="sfa-preview-mini-stats">
                      <div>
                        <span>FIELD</span>
                        <strong>EXECUTION</strong>
                      </div>

                      <div>
                        <span>STATUS</span>
                        <strong>TRACKED</strong>
                      </div>

                      <div>
                        <span>DATA</span>
                        <strong>CAPTURED</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sfa-feature-showcase-footer">
                <span></span>

                <p>
                  Designed for
                  <strong> real-world field execution</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="sfa-key-features-bottom" data-aos="fade-up">
            <div className="sfa-key-features-bottom-line"></div>

            <p>
              Plan.
              <strong> Execute. </strong>
              Capture.
              <strong> Track. </strong>
              Report.
            </p>

            <div className="sfa-key-features-bottom-line"></div>

            <button
              type="button"
              className="sfa-key-features-demo-btn"
              onClick={() => setShowSfaDemo(true)}
            >
              Book a Demo
              <span>→</span>
            </button>
          </div>
        </div>
      </section>
      <SfaBookingModal
        show={showSfaDemo}
        onClose={() => setShowSfaDemo(false)}
      />
    </>
  );
};

export default SfaKeyFeatures;
